import { Tooltip } from '@visx/xychart';

import { UTCDate } from '@blockworks/platform';
import { SeriesConfig } from '@blockworks/platform/api/research/studio';
import { FlexBox, Text } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';

import { aggregators } from '../../../utils';
import { timeFormats } from '../utils';

import { TooltipContainer } from './tooltip-container';

const IntervalTooltip = ({ date, interval }: { date: string; interval?: string; isBaseInterval?: boolean }) => {
    const intervalFormat = timeFormats[interval as keyof typeof timeFormats];
    if (!intervalFormat) {
        return <Text>{timeFormats.days.tooltip(new UTCDate(date))}</Text>;
    }
    return <Text>{intervalFormat.tooltip(new UTCDate(date))}</Text>;
};

const crossHairStyle = { stroke: 'var(--color-content-muted)', strokeWidth: 0.25, strokeDasharray: '2' };

type VisualizationTooltipProps<T extends {}> = {
    visualizationId: string;
    meta: any;
    xAxis: any;
    groups: any[];
    params: any;
    tooltip: any;
    hidden?: Array<keyof T>;
};

function VisualizationTooltip<T extends {}>({
    visualizationId,
    meta,
    xAxis,
    groups,
    params,
    tooltip,
    hidden = [],
}: VisualizationTooltipProps<T>) {
    return (
        <Tooltip
            showDatumGlyph
            showVerticalCrosshair
            showHorizontalCrosshair
            horizontalCrosshairStyle={crossHairStyle}
            verticalCrosshairStyle={crossHairStyle}
            detectBounds
            style={{ background: 'transparent', padding: 0, position: 'absolute' }}
            renderTooltip={({ tooltipData }) => {
                const data = tooltipData?.nearestDatum?.datum;
                const baseInterval = meta?.dimensions[xAxis.column as keyof typeof meta]?.unit;
                const isBaseInterval = baseInterval === params?.timeframe;
                if (!data) return null;

                return (
                    <TooltipContainer>
                        <Text size="xxs" weight="bold">
                            {xAxis.type === 'date' && (
                                <IntervalTooltip
                                    date={data[xAxis.column as keyof typeof data]}
                                    isBaseInterval={isBaseInterval}
                                    interval={params?.timeframe}
                                />
                            )}
                            {xAxis.type === 'enum' && <Text>{data[xAxis.column as keyof typeof data]}</Text>}
                        </Text>
                        <FlexBox col className="divide-y">
                            {groups.flatMap(group => {
                                const groupKey = [visualizationId, group.type, group.name].filter(Boolean).join('-');
                                let seriesToShow: SeriesConfig<any>[] = group.series.filter(
                                    (series: SeriesConfig<any>) => !hidden.includes(series.column as keyof T),
                                );

                                if (tooltip?.autoSort) {
                                    seriesToShow = seriesToShow.sort((a: SeriesConfig<any>, b: SeriesConfig<any>) => {
                                        const valueA = data[a.column as keyof typeof data];
                                        const valueB = data[b.column as keyof typeof data];
                                        return valueA > valueB ? -1 : 1;
                                    });
                                }

                                const renderTotal = () => {
                                    if (seriesToShow.length <= 1) return null;
                                    const total = aggregators.sum(
                                        seriesToShow,
                                        (series: SeriesConfig<any>) => data[series.column as keyof typeof data],
                                    );

                                    return (
                                        <FlexBox>
                                            <Text weight="semibold" size="xxs" color="base">
                                                Total
                                            </Text>
                                            <Bit.div flexGrow={1} />
                                            <Text size="xxs">{group.tooltipFormat(total)}</Text>
                                        </FlexBox>
                                    );
                                };

                                return (
                                    <FlexBox py={1} col key={`${groupKey}-tooltip-group`}>
                                        {seriesToShow.map(({ column, color, label = column }: any) => {
                                            const value = data?.[column as keyof typeof data];

                                            if (!value) return null;
                                            const tooltipText = group.tooltipFormat(value);
                                            return (
                                                <FlexBox
                                                    key={`${groupKey}-${column?.toString()}-tooltip`}
                                                    gap="sm"
                                                    alignItems="center"
                                                >
                                                    <div
                                                        style={{
                                                            width: '6px',
                                                            height: '6px',
                                                            background: color,
                                                            borderRadius: '50%',
                                                        }}
                                                    />
                                                    <Text weight="semibold" size="xxs" color="base">
                                                        {label}
                                                    </Text>
                                                    <Bit.div flexGrow={1} />
                                                    <Text size="xxs">{tooltipText}</Text>
                                                </FlexBox>
                                            );
                                        })}
                                        {tooltip?.showTotal && renderTotal()}
                                    </FlexBox>
                                );
                            })}
                        </FlexBox>
                    </TooltipContainer>
                );
            }}
        />
    );
}

export { VisualizationTooltip };
